import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRouteState } from "../../hooks/useRouteState";
import reportManager from "../../services/report/ReportManager";
import {
  DEFAULT_REPORT_SECTION,
  REPORT_SECTION_LIST,
  REPORT_SECTIONS,
} from "../constants/reportConstants";
import { ROUTE_TYPES } from "../constants/routeConstants";

const TWIN_DOMAIN_ROUTE_TYPES = new Set([
  ROUTE_TYPES.DIGITAL_TWIN,
  ROUTE_TYPES.CHECK,
  ROUTE_TYPES.ALIGN,
  ROUTE_TYPES.VALIDATION,
]);

const ReportContext = createContext(null);

const createEmptySectionState = () =>
  REPORT_SECTION_LIST.reduce((acc, section) => {
    acc[section] = [];
    return acc;
  }, {});

const useObjectUrlRegistry = () => {
  const registryRef = useRef(new Map());

  const revokeUrl = useCallback(url => {
    if (
      url &&
      typeof window !== "undefined" &&
      typeof URL !== "undefined" &&
      typeof URL.revokeObjectURL === "function"
    ) {
      URL.revokeObjectURL(url);
    }
  }, []);

  const revokeById = useCallback(
    id => {
      if (!id) return;
      const url = registryRef.current.get(id);
      if (url) {
        revokeUrl(url);
        registryRef.current.delete(id);
      }
    },
    [revokeUrl]
  );

  const register = useCallback(
    (id, blob) => {
      if (!id) return null;

      revokeById(id);

      if (
        !(blob instanceof Blob) ||
        typeof window === "undefined" ||
        typeof URL === "undefined" ||
        typeof URL.createObjectURL !== "function"
      ) {
        return null;
      }

      const url = URL.createObjectURL(blob);
      registryRef.current.set(id, url);
      return url;
    },
    [revokeById]
  );

  const reset = useCallback(() => {
    registryRef.current.forEach(revokeUrl);
    registryRef.current.clear();
  }, [revokeUrl]);

  return {
    register,
    revokeById,
    reset,
  };
};

const sortEntries = entries =>
  [...entries].sort((a, b) =>
    (a?.createdAt || "").localeCompare(b?.createdAt || "")
  );

export const useReportContext = () => {
  const context = useContext(ReportContext);
  if (!context) {
    throw new Error("useReportContext must be used within a ReportProvider");
  }
  return context;
};

export const ReportProvider = ({ children }) => {
  const [reportData, setReportData] = useState(null);
  const [sections, setSections] = useState(() => createEmptySectionState());
  const [sectionsLoading, setSectionsLoading] = useState(true);

  const { register, revokeById, reset } = useObjectUrlRegistry();
  const { routeType, previousRouteType, params } = useRouteState();

  const inTwinDomain = useMemo(
    () => TWIN_DOMAIN_ROUTE_TYPES.has(routeType),
    [routeType]
  );

  const previousInTwinDomain = useMemo(
    () => previousRouteType && TWIN_DOMAIN_ROUTE_TYPES.has(previousRouteType),
    [previousRouteType]
  );

  const currentTwinKey = useMemo(() => {
    if (!inTwinDomain) {
      return null;
    }

    const twinId = params?.twinId || null;
    if (!twinId) {
      return null;
    }

    const patientId = params?.patientId || "";
    return `${patientId}:${twinId}`;
  }, [inTwinDomain, params]);

  const lastTwinKeyRef = useRef(null);
  const wasInTwinDomainRef = useRef(previousInTwinDomain);

  const sessionId = useMemo(() => {
    const { sessionId: resolvedSessionId } = reportManager.ensureSessionId();
    return resolvedSessionId;
  }, []);

  const convertRecordToEntry = useCallback(
    record => {
      if (!record) return null;
      return {
        id: record.id,
        section: record.section || DEFAULT_REPORT_SECTION,
        createdAt: record.createdAt,
        fileName: record.fileName,
        source: record.source,
        metadata: record.metadata || {},
        blob: record.blob,
        url: register(record.id, record.blob),
      };
    },
    [register]
  );

  const applyRecords = useCallback(
    recordsBySection => {
      const nextState = createEmptySectionState();
      REPORT_SECTION_LIST.forEach(section => {
        const records = recordsBySection?.[section] || [];
        const entries = records.map(convertRecordToEntry).filter(Boolean);
        nextState[section] = sortEntries(entries);
      });
      setSections(nextState);
    },
    [convertRecordToEntry]
  );

  useEffect(() => {
    let mounted = true;

    const loadSections = async () => {
      try {
        const records = await reportManager.initialize();
        if (!mounted) {
          return;
        }
        reset();
        applyRecords(records);
      } finally {
        if (mounted) {
          setSectionsLoading(false);
        }
      }
    };

    const unsubscribe = reportManager.subscribe(event => {
      if (!mounted || !event) {
        return;
      }

      switch (event.type) {
        case "added": {
          const entry = convertRecordToEntry(event.record);
          if (!entry) {
            return;
          }
          setSections(prev => {
            const next = { ...prev };
            const sectionEntries = sortEntries([
              ...(next[entry.section] || []).filter(
                existing => existing.id !== entry.id
              ),
              entry,
            ]);
            next[entry.section] = sectionEntries;
            return next;
          });
          break;
        }
        case "removed": {
          revokeById(event.id);
          setSections(prev => {
            const next = { ...prev };
            const section = event.section || DEFAULT_REPORT_SECTION;
            next[section] = (next[section] || []).filter(
              entry => entry.id !== event.id
            );
            return next;
          });
          break;
        }
        case "section-cleared": {
          const section = event.section || DEFAULT_REPORT_SECTION;
          setSections(prev => {
            const previousEntries = prev[section] || [];
            previousEntries.forEach(entry => revokeById(entry.id));
            if (!previousEntries.length) {
              return prev;
            }
            return {
              ...prev,
              [section]: [],
            };
          });
          break;
        }
        case "cleared": {
          setSections(prev => {
            Object.values(prev).forEach(entries =>
              entries.forEach(entry => revokeById(entry.id))
            );
            return createEmptySectionState();
          });
          break;
        }
        case "reloaded": {
          reset();
          applyRecords(event.recordsBySection);
          break;
        }
        default:
          break;
      }
    });

    loadSections();

    return () => {
      mounted = false;
      unsubscribe();
      setSections(prev => {
        Object.values(prev).forEach(entries =>
          entries.forEach(entry => revokeById(entry.id))
        );
        return createEmptySectionState();
      });
      reset();
    };
  }, [applyRecords, convertRecordToEntry, reset, revokeById]);

  useEffect(() => {
    let shouldClear = false;
    const lastTwinKey = lastTwinKeyRef.current;

    if (inTwinDomain) {
      if (lastTwinKey && currentTwinKey && lastTwinKey !== currentTwinKey) {
        shouldClear = true;
      } else if (lastTwinKey && !currentTwinKey) {
        shouldClear = true;
      }
    } else if (wasInTwinDomainRef.current) {
      shouldClear = true;
    }

    if (shouldClear) {
      (async () => {
        try {
          await reportManager.clearSession();
        } catch (error) {}
      })();
    }

    if (inTwinDomain) {
      lastTwinKeyRef.current = currentTwinKey;
    } else {
      lastTwinKeyRef.current = null;
    }

    wasInTwinDomainRef.current = inTwinDomain;
  }, [inTwinDomain, currentTwinKey]);

  const updateReportData = useCallback(updater => {
    setReportData(prev => {
      if (typeof updater === "function") {
        return updater(prev);
      }
      return updater;
    });
  }, []);

  const resetReportData = useCallback(() => {
    setReportData(null);
  }, []);

  const addImageToSection = useCallback(
    async (section, blob, metadata = {}) => {
      return await reportManager.addBlob({ section, blob, metadata });
    },
    []
  );

  const removeImageById = useCallback(async id => {
    await reportManager.remove(id);
  }, []);

  const clearSection = useCallback(async section => {
    await reportManager.clearSection(section);
  }, []);

  const clearAllSections = useCallback(async () => {
    await reportManager.clearSession();
  }, []);

  const reloadSections = useCallback(async () => {
    reset();
    return await reportManager.reload();
  }, [reset]);

  const value = useMemo(
    () => ({
      reportData,
      updateReportData,
      resetReportData,
      sections,
      sectionsLoading,
      addImageToSection,
      removeImageById,
      clearSection,
      clearAllSections,
      reloadSections,
      sessionId,
      REPORT_SECTIONS,
    }),
    [
      reportData,
      updateReportData,
      resetReportData,
      sections,
      sectionsLoading,
      addImageToSection,
      removeImageById,
      clearSection,
      clearAllSections,
      reloadSections,
      sessionId,
    ]
  );

  return (
    <ReportContext.Provider value={value}>{children}</ReportContext.Provider>
  );
};
